// src/portal-app/operations/reservationsShared.js
//
// Shared primitives for the Reservations surface — the screen-local
// stylesheet, the date/time helpers and the status tone maps.
//
// Extracted VERBATIM from ReservationsScreen.js (Plan 4 Task 7, extraction
// commit). ReservationsScreen.js was 1690 lines — the largest file in the
// app — which put it beyond what can be edited reliably in one pass. The
// split runs along responsibility boundaries:
//
//   reservationsShared.js  — CSS + pure helpers + tone maps (this file)
//   ReservationForms.js    — every write path (guest search/create, create
//                            reservation, seat, add to waitlist)
//   ReservationRows.js     — the read path (how one reservation / one
//                            waiting-list entry renders)
//   ReservationsScreen.js  — state, fetching and layout only
//
// Nothing here reads context or calls a service: these are pure functions
// and constants, importable from any of the three modules above without a
// cycle.

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                             */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.res-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding:8px 0 120px;}
.res-seg{padding:0 16px;margin-bottom:14px;}
.res-date-strip{display:flex;gap:8px;overflow-x:auto;padding:2px 16px;margin-bottom:12px;
  -webkit-overflow-scrolling:touch;scrollbar-width:none;}
.res-date-strip::-webkit-scrollbar{display:none;}
.res-date-chip{flex:none;display:inline-flex;flex-direction:column;align-items:center;
  justify-content:center;min-width:52px;min-height:56px;padding:0 10px;
  border-radius:16px;cursor:pointer;border:none;font-family:inherit;
  background:color-mix(in srgb,var(--c-surface) 40%,transparent);
  -webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px);
  border:1px solid color-mix(in srgb,var(--c-surface) 55%,transparent);
  transition:transform .12s ease,background .15s ease;}
.res-date-chip:active{transform:scale(.96);}
.res-date-chip-active{background:var(--c-green);border-color:var(--c-green);}
.res-date-chip-day{font-size:10px;font-weight:700;text-transform:uppercase;
  letter-spacing:.5px;color:var(--c-textLight);}
.res-date-chip-active .res-date-chip-day{color:rgba(255,255,255,.8);}
.res-date-chip-num{font-size:18px;font-weight:800;color:var(--c-ink);
  font-variant-numeric:tabular-nums;}
.res-date-chip-active .res-date-chip-num{color:var(--c-white);}

/* ---- hero — summarises exactly the list rendered below it ---- */
.res-hero{margin:0 16px 12px;padding:18px 20px 16px;}

/* ---- control row — date strip (leading) + FilterButton (trailing).
       The date strip keeps its own horizontal scroll inside the flex
       child; min-width:0 is what stops it forcing the row wider than
       the viewport and pushing FilterButton off-screen. ---- */
.res-control-row{display:flex;align-items:flex-start;gap:8px;
  padding:0 16px;margin-bottom:10px;}
.res-control-row .res-date-strip{flex:1 1 auto;min-width:0;padding:2px 0;
  margin-bottom:0;}
.res-control-row > .pk-fb{flex:none;margin-block-start:6px;}
/* Waiting list has no date dimension, so its row holds the FilterButton
   alone — pushed to the trailing edge (RTL-safe: flex-end follows the
   writing direction, unlike a hard-coded margin-left:auto). */
.res-control-row-end{justify-content:flex-end;}
.res-control-row-end > .pk-fb{margin-block-start:0;}

/* ---- active-filter summary ---- */
.res-filtersum{display:flex;align-items:center;gap:12px;margin:0 20px 10px;}
.res-filtersum-text{flex:1 1 auto;min-width:0;font-size:12px;font-weight:600;
  color:var(--c-textLight);overflow:hidden;text-overflow:ellipsis;
  white-space:nowrap;}
.res-filtersum-clear{flex:none;min-height:44px;padding-inline:6px;border:none;
  background:transparent;font-family:inherit;font-size:12px;font-weight:700;
  color:var(--c-green);cursor:pointer;}

/* ---- count line ---- */
.res-count-line{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin:2px 20px 10px;}

/* ---- reservation list ---- */
.res-list{display:flex;flex-direction:column;gap:10px;padding:0 16px;}

/* ---- row wrap — CondensedRow plus its status-contextual actions,
       indented to clear the row's 32px icon square + gap ---- */
.res-row-wrap{display:flex;flex-direction:column;gap:6px;}
.res-actions{display:flex;gap:8px;margin-inline-start:44px;flex-wrap:wrap;}
.res-action-btn{min-height:44px;padding:0 14px;border-radius:10px;
  font-family:inherit;font-size:12px;font-weight:700;cursor:pointer;
  transition:background .15s ease,transform .12s ease;}
.res-action-btn:active{transform:scale(.96);}
.res-btn-seat{background:var(--c-green);color:var(--c-white);border:none;}
.res-btn-cancel{border:1.5px solid color-mix(in srgb,var(--c-red) 35%,transparent);
  background:color-mix(in srgb,var(--c-red) 7%,transparent);color:var(--c-red);}
.res-btn-noshow{border:1.5px solid color-mix(in srgb,var(--c-amber) 40%,transparent);
  background:color-mix(in srgb,var(--c-amber) 8%,transparent);color:var(--c-amber);}

/* ---- FAB ---- */
.res-fab{position:fixed;bottom:calc(80px + var(--safe-bottom,0px));
  inset-inline-end:20px;z-index:200;
  display:inline-flex;align-items:center;justify-content:center;gap:6px;
  min-height:52px;padding:0 20px;border-radius:26px;border:none;
  background:var(--c-green);font-family:inherit;font-size:14px;
  font-weight:700;color:var(--c-white);cursor:pointer;
  box-shadow:0 4px 20px color-mix(in srgb,var(--c-ink) 18%,transparent);
  transition:transform .15s ease,box-shadow .15s ease;}
.res-fab:active{transform:scale(.97);}

/* ---- waiting list row actions ---- */
.wl-actions{display:flex;gap:8px;margin-inline-start:44px;flex-wrap:wrap;}
.wl-action-btn{min-height:44px;padding:0 14px;border-radius:10px;
  font-family:inherit;font-size:12px;font-weight:700;cursor:pointer;
  transition:background .15s ease,transform .12s ease;}
.wl-action-btn:active{transform:scale(.96);}
.wl-btn-notify{background:color-mix(in srgb,var(--c-amber) 12%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-amber) 35%,transparent);
  color:var(--c-amber);}
.wl-btn-seat{background:var(--c-green);color:var(--c-white);border:none;}
.wl-btn-noshow{border:1.5px solid color-mix(in srgb,var(--c-red) 35%,transparent);
  background:color-mix(in srgb,var(--c-red) 7%,transparent);color:var(--c-red);}
.wl-btn-left{border:1.5px solid color-mix(in srgb,var(--c-ink) 14%,transparent);
  background:transparent;color:var(--c-textLight);}

/* ---- Bottom sheet (seat form / create form / waitlist form) ---- */
.res-sheet-backdrop{position:fixed;inset:0;z-index:500;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;}
.res-sheet{width:100%;max-width:560px;margin:0 auto;
  padding:20px 20px calc(20px + var(--safe-bottom,0px));
  box-sizing:border-box;border-radius:24px 24px 0 0;
  max-height:90vh;overflow-y:auto;}
.res-sheet-title{font-size:18px;font-weight:800;color:var(--c-ink);
  margin-bottom:14px;letter-spacing:-.3px;}
.res-form-field{margin-bottom:12px;}
.res-form-label{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:.7px;color:var(--c-textLight);margin-bottom:5px;display:block;}
.res-form-input,.res-form-select{display:block;width:100%;box-sizing:border-box;
  min-height:44px;padding:10px 14px;border-radius:14px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 60%,transparent);
  font-family:inherit;font-size:14px;font-weight:500;color:var(--c-ink);
  outline:none;appearance:none;-webkit-appearance:none;}
.res-form-input:focus,.res-form-select:focus{border-color:var(--c-green);}
.res-form-row{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.res-form-actions{display:flex;gap:10px;margin-top:16px;}
.res-form-cancel-btn{flex:1;min-height:44px;border:1.5px solid
  color-mix(in srgb,var(--c-ink) 14%,transparent);border-radius:16px;
  background:transparent;font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-ink);cursor:pointer;}
.res-form-save-btn{flex:1;min-height:44px;border:none;border-radius:16px;
  background:var(--c-green);font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;}
.res-form-save-btn:disabled{opacity:.5;cursor:not-allowed;}
.res-form-error{font-size:12px;font-weight:600;color:var(--c-red);margin-top:8px;}

/* ---- Guest search results ---- */
.res-guest-results{border:1.5px solid color-mix(in srgb,var(--c-ink) 10%,transparent);
  border-radius:14px;overflow:hidden;margin-top:6px;}
.res-guest-result{min-height:44px;padding:10px 14px;cursor:pointer;
  display:flex;flex-direction:column;justify-content:center;
  transition:background .12s ease;}
.res-guest-result:active{background:color-mix(in srgb,var(--c-green) 8%,transparent);}
.res-guest-result+.res-guest-result{border-top:1px solid
  color-mix(in srgb,var(--c-ink) 8%,transparent);}
.res-guest-result-name{font-size:13px;font-weight:700;color:var(--c-ink);}
.res-guest-result-phone{font-size:11px;font-weight:500;color:var(--c-textLight);}
.res-create-guest-btn{min-height:40px;padding:0 14px;border-radius:12px;
  border:1.5px solid color-mix(in srgb,var(--c-green) 35%,transparent);
  background:color-mix(in srgb,var(--c-green) 8%,transparent);
  font-family:inherit;font-size:12px;font-weight:700;color:var(--c-green);
  cursor:pointer;margin-top:8px;
  transition:background .15s ease;}
.res-create-guest-btn:active{background:color-mix(in srgb,var(--c-green) 14%,transparent);}
.res-selected-guest{padding:10px 14px;border-radius:14px;
  background:color-mix(in srgb,var(--c-green) 8%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-green) 25%,transparent);
  margin-bottom:4px;}
.res-selected-guest-name{font-size:13px;font-weight:700;color:var(--c-green);}
.res-selected-guest-change{font-size:11px;font-weight:600;color:var(--c-textLight);
  cursor:pointer;text-decoration:underline;margin-top:2px;border:none;background:transparent;
  padding:0;font-family:inherit;}

/* ---- Stepper ---- */
.res-stepper{display:flex;align-items:center;gap:12px;}
.res-stepper-btn{min-width:44px;min-height:44px;border-radius:10px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 14%,transparent);
  background:transparent;font-family:inherit;font-size:18px;font-weight:700;
  color:var(--c-ink);cursor:pointer;display:inline-flex;
  align-items:center;justify-content:center;}
.res-stepper-val{font-size:16px;font-weight:800;color:var(--c-ink);
  font-variant-numeric:tabular-nums;min-width:24px;text-align:center;}

/* ---- animations ---- */
@keyframes resSlideUp{
  from{transform:translateY(100%);opacity:0;}
  to{transform:translateY(0);opacity:1;}
}
.res-sheet{animation:resSlideUp 260ms ease-out both;}

@media(prefers-reduced-motion:reduce){
  .res-sheet{animation:none;}
  .res-date-chip:active,.res-action-btn:active,.wl-action-btn:active,
  .res-fab:active,.res-create-guest-btn:active{
    transform:none;transition:none;}
}
`;

let cssInjected = false;

/** Inject the Reservations stylesheet once per document. */
export function ensureResCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'res-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Format a local-time ISO string from scheduled_for → 12h AM/PM display.
 *  CRITICAL: scheduled_for is LOCAL time, never UTC. We append T00:00:00
 *  guard only when no T is present (date-only string from older data).
 *  Do NOT use toISOString() anywhere in this file.
 */
export function fmtScheduledFor(scheduled_for) {
    if (!scheduled_for) return '';
    try {
        // Ensure we parse as LOCAL time: append T00:00:00 if date-only
        const localStr = scheduled_for.includes('T') ? scheduled_for : `${scheduled_for}T00:00:00`;
        const d = new Date(localStr);
        return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch (_) {
        return '';
    }
}

/** Parse a scheduled_for / joined_at string as LOCAL time. Returns null when
 *  the value is missing or unparseable, so callers can skip the record rather
 *  than propagate a NaN into a comparison (which silently reads as false and
 *  would quietly drop reservations from the "next upcoming" calculation). */
export function parseLocal(iso) {
    if (!iso) return null;
    try {
        const localStr = iso.includes('T') ? iso : `${iso}T00:00:00`;
        const d = new Date(localStr);
        return Number.isNaN(d.getTime()) ? null : d;
    } catch (_) {
        return null;
    }
}

/** Get today's date as YYYY-MM-DD using LOCAL date components (never toISOString). */
export function localDateStr(offsetDays = 0) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Build a date chip label for the date strip. */
export function dateChipLabel(offsetDays, t) {
    const days = [
        t('day_sun', {}, 'Sun'),
        t('day_mon', {}, 'Mon'),
        t('day_tue', {}, 'Tue'),
        t('day_wed', {}, 'Wed'),
        t('day_thu', {}, 'Thu'),
        t('day_fri', {}, 'Fri'),
        t('day_sat', {}, 'Sat'),
    ];
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    if (offsetDays === 0) return { day: t('today', {}, 'Today'), num: d.getDate() };
    return { day: days[d.getDay()], num: d.getDate() };
}

/** Compute elapsed wait time from joined_at local ISO string. */
export function fmtWaitTime(joined_at, t) {
    if (!joined_at) return '';
    try {
        const localStr = joined_at.includes('T') ? joined_at : `${joined_at}T00:00:00`;
        const diff = Math.floor((Date.now() - new Date(localStr).getTime()) / 60000);
        if (diff < 1) return t('wait_time', {}, 'Just now');
        return `${diff}m`;
    } catch (_) {
        return '';
    }
}

/* ------------------------------------------------------------------ */
/* Status tone maps                                                    */
/* ------------------------------------------------------------------ */
export const RES_TONE = {
    booked: 'blue',
    seated: 'green',
    completed: 'muted',
    cancelled: 'red',
    no_show: 'amber',
};

export const WL_TONE = {
    waiting: 'blue',
    notified: 'amber',
    seated: 'green',
    left: 'muted',
    no_show: 'muted',
};

/* ------------------------------------------------------------------ */
/* Status filter vocabulary (reservation-specific statuses)            */
/* ------------------------------------------------------------------ */
export const RES_STATUSES = ['booked', 'seated', 'completed', 'cancelled', 'no_show'];
